const MIN_KEYBOARD_HEIGHT = 120;
const MIN_KEYBOARD_RATIO = 0.18;

const subscribers = new Set();
const orientationBaselines = new Map();
let keyboardVisible = false;
let detachListeners = null;
let animationFrame = null;
let orientationTimer = null;
let lastScrolledElement = null;

export function isEditableControl(element) {
  if (!element || element.disabled || element.readOnly) return false;
  if (element.isContentEditable) return true;
  if (element.tagName === 'TEXTAREA') return true;
  if (element.tagName !== 'INPUT') return false;

  return !['button', 'checkbox', 'file', 'hidden', 'radio', 'range', 'reset', 'submit'].includes(
    String(element.type || 'text').toLowerCase()
  );
}

export function isSoftwareKeyboardMeasurement({
  editableFocused,
  touchCapable,
  viewportHeight,
  referenceHeight,
  viewportWidth,
  referenceWidth
}) {
  if (!editableFocused || !touchCapable) return false;
  if (!Number.isFinite(viewportHeight) || !Number.isFinite(referenceHeight)) return false;
  if (viewportHeight <= 0 || referenceHeight <= 0) return false;
  if (Number.isFinite(viewportWidth) && Number.isFinite(referenceWidth) && referenceWidth > 0) {
    const widthChange = Math.abs(referenceWidth - viewportWidth);
    if (widthChange >= Math.max(80, referenceWidth * 0.2)) return false;
  }

  const heightLoss = referenceHeight - viewportHeight;
  return heightLoss >= Math.max(MIN_KEYBOARD_HEIGHT, referenceHeight * MIN_KEYBOARD_RATIO);
}

function orientationKey(windowObject, viewport) {
  const type = windowObject.screen?.orientation?.type;
  if (type) return type.startsWith('landscape') ? 'landscape' : 'portrait';
  return viewport.width > viewport.height ? 'landscape' : 'portrait';
}

function isTouchCapable(windowObject) {
  return Boolean(
    windowObject.navigator?.maxTouchPoints > 0
    || windowObject.matchMedia?.('(pointer: coarse)').matches
  );
}

function publish(nextValue) {
  if (nextValue === keyboardVisible) return;
  keyboardVisible = nextValue;
  subscribers.forEach((subscriber) => subscriber());
}

function keepFocusedControlVisible(documentObject) {
  const activeElement = documentObject.activeElement;
  if (!isEditableControl(activeElement) || activeElement === lastScrolledElement) return;
  lastScrolledElement = activeElement;
  activeElement.scrollIntoView?.({block: 'center', inline: 'nearest', behavior: 'auto'});
}

function measure(windowObject, documentObject) {
  const viewport = windowObject.visualViewport;
  if (!viewport || documentObject.visibilityState === 'hidden') {
    lastScrolledElement = null;
    publish(false);
    return;
  }

  const key = orientationKey(windowObject, viewport);
  const screenHeight = Number(windowObject.screen?.height) || 0;
  const priorBaseline = orientationBaselines.get(key);
  const referenceHeight = priorBaseline?.height || Math.max(screenHeight, viewport.height);
  const referenceWidth = priorBaseline?.width || viewport.width;
  const editableFocused = isEditableControl(documentObject.activeElement);
  const nextVisible = isSoftwareKeyboardMeasurement({
    editableFocused,
    touchCapable: isTouchCapable(windowObject),
    viewportHeight: viewport.height,
    referenceHeight,
    viewportWidth: viewport.width,
    referenceWidth
  });

  if (!nextVisible) {
    orientationBaselines.set(key, {
      height: Math.max(priorBaseline?.height || 0, viewport.height),
      width: viewport.width
    });
    lastScrolledElement = null;
  }

  publish(nextVisible);
  if (nextVisible) keepFocusedControlVisible(documentObject);
}

function attach(windowObject, documentObject) {
  const viewport = windowObject.visualViewport;
  if (!viewport) return () => {};

  const scheduleMeasure = () => {
    if (animationFrame !== null) windowObject.cancelAnimationFrame(animationFrame);
    animationFrame = windowObject.requestAnimationFrame(() => {
      animationFrame = null;
      measure(windowObject, documentObject);
    });
  };
  const handleVisibility = () => {
    if (documentObject.visibilityState === 'hidden') publish(false);
    scheduleMeasure();
  };
  const handleOrientation = () => {
    publish(false);
    scheduleMeasure();
    if (orientationTimer !== null) windowObject.clearTimeout(orientationTimer);
    orientationTimer = windowObject.setTimeout(scheduleMeasure, 250);
  };

  viewport.addEventListener('resize', scheduleMeasure);
  windowObject.addEventListener('resize', scheduleMeasure);
  windowObject.addEventListener('orientationchange', handleOrientation);
  windowObject.addEventListener('pageshow', scheduleMeasure);
  documentObject.addEventListener('focusin', scheduleMeasure);
  documentObject.addEventListener('focusout', scheduleMeasure);
  documentObject.addEventListener('visibilitychange', handleVisibility);
  measure(windowObject, documentObject);

  return () => {
    viewport.removeEventListener('resize', scheduleMeasure);
    windowObject.removeEventListener('resize', scheduleMeasure);
    windowObject.removeEventListener('orientationchange', handleOrientation);
    windowObject.removeEventListener('pageshow', scheduleMeasure);
    documentObject.removeEventListener('focusin', scheduleMeasure);
    documentObject.removeEventListener('focusout', scheduleMeasure);
    documentObject.removeEventListener('visibilitychange', handleVisibility);
    if (animationFrame !== null) windowObject.cancelAnimationFrame(animationFrame);
    if (orientationTimer !== null) windowObject.clearTimeout(orientationTimer);
    animationFrame = null;
    orientationTimer = null;
    lastScrolledElement = null;
    publish(false);
  };
}

export function subscribeToSoftwareKeyboard(subscriber) {
  subscribers.add(subscriber);
  if (subscribers.size === 1 && typeof window !== 'undefined' && typeof document !== 'undefined') {
    detachListeners = attach(window, document);
  }

  return () => {
    subscribers.delete(subscriber);
    if (subscribers.size === 0 && detachListeners) {
      detachListeners();
      detachListeners = null;
    }
  };
}

export function getSoftwareKeyboardSnapshot() {
  return keyboardVisible;
}
